import { paths, ReservoirChain, reservoirChains, ReservoirClientOptions } from '@reservoir0x/reservoir-sdk'
import { env } from 'environments'
import { mainnet } from 'wagmi/chains'
import { BASE_URL } from './routes'

/**
 * Converts a token ID from the database format to the Reservoir API format.
 * Our database stores IDs with hyphens (-), while Reservoir API expects colons (:).
 * Format: {contractAddress}-{tokenId} → {contractAddress}:{tokenId}
 * @param id - The token ID in database format (e.g., "0x1234...abcd-123")
 *            where first part is lowercase contract address and second part is token ID
 * @returns The token ID in Reservoir API format (e.g., "0x1234...abcd:123")
 */
export const getReservoirTokenId = (id: string): string => {
  return id.replace('-', ':')
}

function getReservoirChain(): ReservoirChain {
  if (env.chain.id === mainnet.id) {
    return {
      ...reservoirChains.mainnet,
      active: true,
      baseApiUrl: `${BASE_URL}/api/reservoir`
    }
  }

  return {
    ...reservoirChains.sepolia,
    active: true,
    baseApiUrl: `${BASE_URL}/api/reservoir`
  }
}

/**
 * Generates a Reservoir collection ID for an Art Blocks project.
 * Reservoir uses collection IDs to represent a range of tokens within a contract.
 * For Art Blocks, this range corresponds to all tokens within a specific project.
 * Format: {contractAddress}:{startTokenId}:{endTokenId}
 *
 * Note: Handles a special case for project ID 0.
 *
 * @param contractAddress - The Ethereum address of the Art Blocks contract.
 * @param artblocksProjectId - The Art Blocks project ID *as represented on the contract*, not the internal database ID.
 * @returns The Reservoir collection ID string.
 */
export function getReservoirCollectionId(contractAddress: string, artblocksProjectId: string): string {
  let startToken = `${artblocksProjectId}000000`
  let endToken = `${artblocksProjectId}999999`
  if (Number(artblocksProjectId) === 0) {
    startToken = `0`
    endToken = `999999`
  }

  return `${contractAddress}:${startToken}:${endToken}`
}

export const RESERVOIR_CLIENT_OPTIONS: ReservoirClientOptions = {
  chains: [getReservoirChain()],
  source: 'artblocks.io',
  marketplaceFees: [`${env.MARKETPLACE_FEE_ADDRESS}:0`],
  automatedRoyalties: true,
  normalizeRoyalties: false
}

export const RESERVOIR_BIDS_ENDPOINT = `${env.RESERVOIR_BASE_URL_HTTP}/orders/bids/v6`
export type ReservoirBidsResponse = paths['/orders/bids/v6']['get']['responses']['200']['schema']['orders']
export type ReservoirBid = NonNullable<ReservoirBidsResponse>[number]
